import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';

// Angular and other external libraries
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { AlertModule } from 'ngx-bootstrap/alert'
import { InputTextModule } from "primeng/inputtext";
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// Main app components
import { AppRoutingModule, appRouterProviders } from './app-routing.module';
import { CAPDAdminComponent } from './capd-admin-component/capd-admin.component';
import { CDSTreeComponent } from './cds_tree/cds-tree.component';
import { LoginComponent } from './login/login.component';
import { ClientDashboardComponent } from "./client-dashboard/client-dashboard.component";



// Services
import { CDSTreeService } from './services/cds.service';
import { OauthService } from './services/oauth.service';
import { OrgService } from './services/org.service';
import { PackageService } from './services/package.service';
import { RulesService } from './services/rules.service';
import { UserService } from './services/user.service';

import { AppComponent } from './app.component';
import { CreateRequestGuardService } from './services/my-guard.service';
import {MModalManagerComponent} from "./mmodal-manager/mmodal-manager.component";
import {LibraryComponent} from "./rule/rule.component";
import {LibraryComponentV2} from "./rule/v2/library.component";
import {RuleDetailComponent} from "./rule/rule-detail.component";
import {LibraryRuleDetailComponent} from "./rule/v2/library-rule-detail.component";
import {RuleDetailReadOnlyComponent} from "./rule/rule-detail-read.component";
import {RuleDeleteComponent} from "./rule/rule-delete.component";
import {RuleTriggerEditComponent} from "./rule/rule-trigger-edit.component";
import {DefaultMessageEditorComponent} from "./fd-message-editor/fd-message-editor.component";
import {NotereaderMessageEditorComponent} from "./epic-message-editor/epic-message-editor.component";
import {RuleAddComponent} from "./rule/rule-add.component";
import {PackageComponent} from "./package/package.component";
import {PackageDetailComponent} from "./package/package-detail.component";
import {PackageRuleTrayComponent} from "./package/package-rule-tray.component";
import {PackageRuleDetailComponent} from "./package/package-rule-detail.component";
import {PackageAddComponent} from "./package/package-add.component";
import {PackageDuplicateComponent} from "./package/package-duplicate.component";
import {PackagePublishComponent} from "./package/package-publish.component";
import {CompanyContentComponent} from "./company/company.component";
import {CompanyContentComponentV2} from "./company/v2/company.component";
import {CompanyDetailComponent} from "./company/company-detail.component";
import {GroupContentComponent} from "./group/group.component";
import {GroupContentComponentV2} from "./group/v2/group.component";
import {GroupRuleModalComponent} from "./group/group-rule-modal.component";
import {GroupDeleteRulesComponent} from "./group/group-delete-rules.component";
import {CompanyAssignPackageComponent} from "./company/company-assign-package.component";
import {CompanyAssignRuleComponent} from "./company/company-assign-rule.component";
import {CompanyRuleModalComponent} from "./company/company-rule-modal.component";
import {CompanyFdMessageEditorComponent} from "./company/company-fd-message-editor.component";
import {CompanyNrMessageEditorComponent} from "./company/company-nr-message-editor.component";
import {CompanyDeletePackageComponent} from "./company/company-delete-package.component";
import {UpdateComponent} from "./update/update.component";
import {UpdateDetailComponent} from "./update/update-detail.component";
import {SettingsComponent} from "./settings/settings.component";
import {UserComponent} from "./users/user.component";
import {UserListComponent} from "./users/user-list.component";
import {UserConfigComponent} from "./users/user-config.component";
import {ClientManagerComponent} from "./client-manager/client-manager.component";
import {TenantMappingsComponent} from "./cds_tenant_mapping/tenant_mappings_org.component";
import {TenantMappingsRootComponent} from "./cds_tenant_mapping/tenant_mappings_root.component";
import {RulePreviewComponent} from "./card/card.component";
import {CommonModule} from "@angular/common";
import {RouterModule} from "@angular/router";
import {NgIdleKeepaliveModule} from "@ng-idle/keepalive";
import {CapdClientMock} from "./capd-client-mock/capd-client-mock.component";
import {ClientMockSubmitComponent} from "./capd-client-mock/capd-client-submission/capd-client-submission.components";
import {ClientMockResponseComponent} from "./capd-client-mock/capd-client-response/capd-client-response.component";
import { ClientMockResponseCardComponent } from './capd-client-mock/response-cards/response-card.component';
import {OrgDataService} from "./services/org-data.service";
import {GlobalHttpInterceptorService} from "./services/global-http-interceptor.service";
import {QuillModule} from "ngx-quill";
import { MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { MessagesModule, Messages } from 'primeng/messages'
import {ConfirmationService, MessageService} from "primeng/api";
import {AlertService} from "./services/alert.service";
import {ConfirmDialogModule} from "primeng/confirmdialog";
import {CompanyAssignContentComponent} from './company/v2/company-assign-content.component';
import { AuthGuardService } from './services/auth-guard.service';
import {RulePreviewV2Component} from './card/v2/rule-preview.component';
import {CButtonModule} from "@telesis/cobalt";

export function restoreSessionFactory(oauthService: OauthService) {
  return () => oauthService.restoreSessionOnStartup();
}

@NgModule({
    declarations: [
        AppComponent,
        CAPDAdminComponent,
        CDSTreeComponent,
        LoginComponent,
        MModalManagerComponent,
        LibraryComponent,
        LibraryComponentV2,
        RuleDetailComponent,
        LibraryRuleDetailComponent,
        RuleDetailReadOnlyComponent,
        RuleDeleteComponent,
        RuleTriggerEditComponent,
        DefaultMessageEditorComponent,
        NotereaderMessageEditorComponent,
        RuleAddComponent,
        PackageComponent,
        PackageDetailComponent,
        PackageRuleTrayComponent,
        PackageRuleDetailComponent,
        PackageAddComponent,
        PackageDuplicateComponent,
        PackagePublishComponent,
        CompanyContentComponent,
        CompanyContentComponentV2,
        CompanyDetailComponent,
        GroupContentComponent,
        GroupContentComponentV2,
        GroupRuleModalComponent,
        GroupDeleteRulesComponent,
        CompanyAssignPackageComponent,
        CompanyAssignRuleComponent,
        CompanyRuleModalComponent,
        CompanyFdMessageEditorComponent,
        CompanyNrMessageEditorComponent,
        CompanyDeletePackageComponent,
        UpdateComponent,
        UpdateDetailComponent,
        SettingsComponent,
        UserComponent,
        UserListComponent,
        UserConfigComponent,
        ClientManagerComponent,
        TenantMappingsComponent,
        TenantMappingsRootComponent,
        RulePreviewComponent,
        CapdClientMock,
        ClientMockSubmitComponent,
        ClientMockResponseComponent,
        ClientMockResponseCardComponent,
        ClientDashboardComponent,
        CompanyAssignContentComponent,
        RulePreviewV2Component
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        HttpClientModule,
        [TooltipModule.forRoot()],
        [AlertModule.forRoot()],
        AppRoutingModule,
        ReactiveFormsModule,
        CommonModule,
        RouterModule,
        MenuModule,
        ToastModule,
        MessagesModule,
        InputTextModule,
        NgIdleKeepaliveModule.forRoot(),
        QuillModule.forRoot({
            modules: {
                toolbar: {
                    container: [
                        ['bold', 'italic', 'underline'],
                        [{'list': 'ordered'}, {'list': 'bullet'}],
                        ['link']
                    ],
                    handlers: {
                        'emoji': function () {
                        }
                    }
                }
            }
        }),
        ConfirmDialogModule,
        CButtonModule
    ],
  providers: [
    AlertService,
    MessageService,
    ConfirmationService,
    appRouterProviders,
    CDSTreeService,
    OauthService,
    AuthGuardService,
    OrgService,
    PackageService,
    RulesService,
    UserService,
    CreateRequestGuardService,
    TenantMappingsComponent,
    OrgDataService,
    MessageService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GlobalHttpInterceptorService,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: restoreSessionFactory,
      deps: [OauthService],
      multi: true
    },
    ConfirmDialogModule
  ],
  exports: [
    AppComponent,
    CAPDAdminComponent,
    CDSTreeComponent,
    LoginComponent,
    MModalManagerComponent,
    LibraryComponent,
    LibraryComponentV2,
    RuleDetailComponent,
    LibraryRuleDetailComponent,
    RuleDetailReadOnlyComponent,
    RuleDeleteComponent,
    RuleTriggerEditComponent,
    DefaultMessageEditorComponent,
    NotereaderMessageEditorComponent,
    RuleAddComponent,
    PackageComponent,
    PackageDetailComponent,
    PackageRuleTrayComponent,
    PackageRuleDetailComponent,
    PackageAddComponent,
    PackageDuplicateComponent,
    PackagePublishComponent,
    CompanyContentComponent,
    CompanyContentComponentV2,
    CompanyDetailComponent,
    GroupContentComponent,
    GroupContentComponentV2,
    GroupRuleModalComponent,
    GroupDeleteRulesComponent,
    CompanyAssignPackageComponent,
    CompanyAssignRuleComponent,
    CompanyRuleModalComponent,
    CompanyFdMessageEditorComponent,
    CompanyNrMessageEditorComponent,
    CompanyDeletePackageComponent,
    UpdateComponent,
    UpdateDetailComponent,
    SettingsComponent,
    UserComponent,
    UserListComponent,
    UserConfigComponent,
    ClientManagerComponent,
    TenantMappingsComponent,
    TenantMappingsRootComponent,
    RulePreviewComponent,
    CompanyAssignContentComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
